/**
 * Orchestra AI Platform - Property Panel Component
 * 
 * Panel for editing node properties and parameters
 * 
 * @fileoverview Property panel for configuring selected nodes
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useWorkflowStore } from '../../store/workflowStore';
import toast from 'react-hot-toast';
import './PropertyPanel.css';

/**
 * @typedef {Object} Parameter
 * @property {string} name
 * @property {string} type
 * @property {boolean} required
 * @property {string} [description]
 * @property {*} [defaultValue]
 */

/**
 * Parameter Editor Component
 * @param {Object} props
 * @param {Parameter} props.parameter
 * @param {*} props.value
 * @param {Function} props.onChange
 * @param {Object[]} props.availableVariables
 */
function ParameterEditor({ parameter, value, onChange, availableVariables = [] }) {
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  /**
   * Handle input change
   * @param {React.ChangeEvent} event
   */
  const handleChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  /**
   * Insert variable
   * @param {string} variable
   */
  const insertVariable = (variable) => {
    const newValue = `${inputValue}{{${variable}}}`;
    setInputValue(newValue);
    onChange(newValue);
  };

  return (
    <div className="parameter-editor">
      <label className="parameter-editor-label">
        {parameter.name}
        {parameter.required && <span className="parameter-editor-required">*</span>}
      </label>
      {parameter.description && (
        <p className="parameter-editor-description">{parameter.description}</p>
      )}
      <div className="parameter-editor-input-group">
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={parameter.defaultValue || `Enter ${parameter.name}`}
          className="parameter-editor-input"
        />
        {availableVariables.length > 0 && (
          <select
            onChange={(e) => {
              if (e.target.value) {
                insertVariable(e.target.value);
                e.target.value = '';
              }
            }}
            className="parameter-editor-select"
          >
            <option value="">Insert Variable</option>
            {availableVariables.map((var_) => (
              <option key={var_.id} value={var_.id}>
                {var_.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="parameter-editor-type">Type: {parameter.type}</p>
    </div>
  );
}

ParameterEditor.propTypes = {
  parameter: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    description: PropTypes.string,
    defaultValue: PropTypes.any,
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  availableVariables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
};

/**
 * Property Panel Component
 * @param {Object} props
 */
export function PropertyPanel({ onClose }) {
  const { nodes, selectedNodeId, updateNode, selectNode } = useWorkflowStore();
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  const [nodeName, setNodeName] = useState('');
  const [parameters, setParameters] = useState({});

  useEffect(() => {
    if (selectedNode) {
      setNodeName(selectedNode.data.label || '');
      setParameters(selectedNode.data.parameters || {});
    }
  }, [selectedNode]);

  if (!selectedNode) {
    return (
      <div className="property-panel">
        <div className="property-panel-content">
          <p className="property-panel-empty">Select a node to edit properties</p>
        </div>
      </div>
    );
  }

  /**
   * Get available variables from previous nodes
   */
  const getAvailableVariables = () => {
    // TODO: Get variables from connected nodes
    return [];
  };

  /**
   * Handle parameter change
   * @param {string} paramName
   * @param {*} value
   */
  const handleParameterChange = (paramName, value) => {
    setParameters((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  };

  /**
   * Save changes
   */
  const handleSave = () => {
    updateNode(selectedNodeId, {
      data: {
        ...selectedNode.data,
        label: nodeName,
        parameters,
      },
    });
    toast.success('Node updated');
  };

  /**
   * Test API call
   */
  const handleTest = async () => {
    try {
      // TODO: Call test API endpoint
      toast.success('Test completed');
    } catch (error) {
      toast.error('Test failed: ' + error.message);
    }
  };

  // Get API parameters (mock for now)
  const apiParameters = selectedNode.data.endpoint
    ? [
        { name: 'text', type: 'string', required: true, description: 'Input text' },
        { name: 'options', type: 'object', required: false, description: 'Additional options' },
      ]
    : [];

  return (
    <div className="property-panel">
      <div className="property-panel-header">
        <div className="property-panel-header-content">
          <h2 className="property-panel-title">Node Properties</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="property-panel-close"
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>

        {/* Node Name */}
        <div className="property-panel-field">
          <label className="property-panel-label">Node Name</label>
          <input
            type="text"
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            className="property-panel-input"
          />
        </div>

        {/* Server and Endpoint (read-only) */}
        {selectedNode.data.server && (
          <div className="property-panel-field">
            <label className="property-panel-label">Server</label>
            <input
              type="text"
              value={selectedNode.data.server}
              disabled
              className="property-panel-input"
            />
          </div>
        )}

        {selectedNode.data.endpoint && (
          <div className="property-panel-field">
            <label className="property-panel-label">Endpoint</label>
            <code className="property-panel-code">
              {selectedNode.data.endpoint}
            </code>
          </div>
        )}
      </div>

      {/* Parameters */}
      <div className="property-panel-content">
        <h3 className="property-panel-section-title">Parameters</h3>
        {apiParameters.length === 0 ? (
          <p className="property-panel-empty">No parameters available</p>
        ) : (
          apiParameters.map((param) => (
            <ParameterEditor
              key={param.name}
              parameter={param}
              value={parameters[param.name]}
              onChange={(value) => handleParameterChange(param.name, value)}
              availableVariables={getAvailableVariables()}
            />
          ))
        )}
      </div>

      {/* Actions */}
      <div className="property-panel-actions">
        <button
          onClick={handleSave}
          className="property-panel-button property-panel-button-save"
        >
          Save Changes
        </button>
        {selectedNode.data.endpoint && (
          <button
            onClick={handleTest}
            className="property-panel-button property-panel-button-test"
          >
            Test API
          </button>
        )}
      </div>
    </div>
  );
}

PropertyPanel.propTypes = {
  onClose: PropTypes.func,
};

